import { useRefreshTrigger } from "@/composables/refreshTrigger";
import { progressEvent, useProgressEvents } from "@/composables/useProgressEvents";
import { $t } from "@/i18n";
import { useAsyncOperationProgress } from "@/store/asyncOperationProgress";
import { useWorkspace } from "@/store/workspace";
import { toPercentage } from "@/utils/progressPercentage";
import { showToastForError, showToastForSuccess } from "@/utils/toast";

const EVENT_CODE_PROGRESS = "tenant_delete.progress";
const EVENT_CODE_FINISHED = "tenant_delete.finished";
export const TENANT_DELETE_OPERATION_KIND = "tenant-delete";

interface TenantDeleteProgressPayload {
  tenantId: string;
  completed: number;
  total: number;
}

interface TenantDeleteFinishedPayload extends TenantDeleteProgressPayload {
  error?: string;
}

function isProgressPayload(data: unknown): data is TenantDeleteProgressPayload {
  return (
    !!data &&
    typeof data === "object" &&
    typeof (data as TenantDeleteProgressPayload).tenantId === "string" &&
    typeof (data as TenantDeleteProgressPayload).completed === "number" &&
    typeof (data as TenantDeleteProgressPayload).total === "number"
  );
}

function isFinishedPayload(data: unknown): data is TenantDeleteFinishedPayload {
  return isProgressPayload(data);
}

/**
 * Feeds asyncOperationProgress's tenant-delete operations from the SSE stream (#3572), matched by
 * tenantId. Events for a tenant this tab did not delete are ignored: a failed copy also deletes its
 * destination in the background and publishes the same events.
 */
export default function useTenantDeleteProgressEvents() {
  const progress = useAsyncOperationProgress();
  const workspace = useWorkspace();
  const refetchList = useRefreshTrigger();

  const isOwnOperation = (tenantId: string) =>
    progress.operations.some((o) => o.id === tenantId && o.kind === TENANT_DELETE_OPERATION_KIND);

  function handleFinished(data: TenantDeleteFinishedPayload) {
    if (!isOwnOperation(data.tenantId)) return;

    refetchList();
    void workspace.tenantsRefetch();

    if (data.error) {
      progress.remove(data.tenantId);
      showToastForError($t("tenantManagement.delete.label.deleteFailedToast"));
      return;
    }

    progress.finish(data.tenantId);
    showToastForSuccess($t("tenantManagement.delete.label.deleteSuccessToast"));
  }

  return useProgressEvents([
    progressEvent(EVENT_CODE_PROGRESS, isProgressPayload, (data) => {
      if (!isOwnOperation(data.tenantId)) return;
      progress.update(data.tenantId, { percentage: toPercentage(data.completed, data.total) });
    }),
    progressEvent(EVENT_CODE_FINISHED, isFinishedPayload, handleFinished),
  ]);
}
